import React, { useCallback, useRef, useState } from 'react';
import { Subscribe } from 'unstated';

import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { expValEquals } from '@confluence/feature-experiments';
import { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
// eslint-disable-next-line import/no-unresolved -- package entry-point exists under page-pdf-export/entry-points
import { PagePdfExportModal } from '@confluence/page-pdf-export/entry-points/PagePdfExportModal.ts';
// eslint-disable-next-line import/no-unresolved -- package entry-point exists under page-pdf-export/entry-points
import { useCreatePagePdfExportTask } from '@confluence/page-pdf-export/entry-points/useCreatePagePdfExportTask.ts';

export type ExportPdfMenuItemProps = {
	contentId: string;
	/** Content type of the page (e.g. `page`, `blogpost`); used to build the correct content ARI. Live docs are `page`. */
	contentType?: string;
	/** The legacy hidden-action export URL from the web item; used when the experiment is off or the backend selects a legacy (V1/V2) export path. */
	url: string;
	label?: string;
	onClickAnalytics?: () => void;
	closeParentMenu?: () => void;
};

export const ExportPdfMenuItem: React.FC<ExportPdfMenuItemProps> = ({
	contentId,
	contentType,
	url,
	label,
	onClickAnalytics,
	closeParentMenu,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	// `isCreating` drives the item's disabled state (needs a re-render); `isCreatingRef` is the
	// in-flight guard read inside the click handler, so the handler doesn't depend on the state and
	// isn't recreated on every toggle.
	const [isCreating, setIsCreating] = useState(false);
	const isCreatingRef = useRef(false);

	// Shared create → resolve flow (also used by the Cmd/Ctrl+P print shortcut, CCCEMAU-3178).
	// Defining the hook issues no network request — it only fires when `createAndResolve` runs.
	const { createAndResolve } = useCreatePagePdfExportTask();

	const fireClicked = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'exportMenuItem',
				actionSubjectId: 'exportPdf',
				source: 'overflowMenu',
				attributes: { exportType: 'pdf', contentId },
			},
		}).fire();
	}, [createAnalyticsEvent, contentId]);

	const fireResult = useCallback(
		(action: 'succeeded' | 'failed', attributes?: Record<string, unknown>) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action,
					actionSubject: 'exportMenuItem',
					actionSubjectId: 'exportPdf',
					source: 'overflowMenu',
					attributes: { exportType: 'pdf', contentId, ...attributes },
				},
			}).fire();
		},
		[createAnalyticsEvent, contentId],
	);

	const handleClick = useCallback(
		async (
			event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
			dialogs: DialogsStateContainer,
		) => {
			onClickAnalytics?.();
			fireClicked();

			// Read the experiment only on user click — never at render — so exposure fires exactly at
			// the divergence point, symmetrically for both arms.
			if (!expValEquals('cc_mohiti_pdf_export_exp', 'isEnabled', true)) {
				// Experiment off: preserve the existing hidden-action URL behaviour (native <a href>)
				// and skip all GraphQL. Do NOT preventDefault so the anchor navigates as it does today.
				closeParentMenu?.();
				return;
			}

			// Experiment on: we own navigation now.
			event.preventDefault();

			if (isCreatingRef.current) {
				return;
			}
			isCreatingRef.current = true;
			setIsCreating(true);

			try {
				const decision = await createAndResolve(contentId, contentType);

				closeParentMenu?.();

				switch (decision.type) {
					case 'legacy':
						// V1/V2 behave exactly like the experiment-off path: navigate to the existing
						// hidden action URL. UNSAFE_shouldDisableRouterLink means this is a full-page nav.
						fireResult('succeeded', { decision: 'legacy' });
						window.location.assign(url);
						break;
					case 'modal':
						fireResult('succeeded', { decision: 'modal' });
						dialogs.showModal(PagePdfExportModal, {
							contentId,
							exportTaskId: decision.exportTaskId,
							source: 'menu',
						});
						break;
					case 'error':
					default:
						fireResult('failed', {
							reason: decision.type === 'error' ? decision.reason : 'unknown',
						});
						// Open the modal with a missing task id so it renders its built-in error state.
						// Do not automatically retry or fall back to the hidden action.
						dialogs.showModal(PagePdfExportModal, {
							contentId,
							exportTaskId: null,
							source: 'menu',
						});
						break;
				}
			} finally {
				// Always reset so an unexpected throw can't leave the item permanently disabled.
				isCreatingRef.current = false;
				setIsCreating(false);
			}
		},
		[
			onClickAnalytics,
			fireClicked,
			fireResult,
			createAndResolve,
			contentId,
			contentType,
			url,
			closeParentMenu,
		],
	);

	return (
		<Subscribe to={[DialogsStateContainer]}>
			{(dialogs: DialogsStateContainer) => (
				<DropdownItem
					href={url}
					UNSAFE_shouldDisableRouterLink
					isDisabled={isCreating}
					testId="export-pdf-menu-item"
					onClick={(event) => {
						void handleClick(event, dialogs);
					}}
				>
					{label}
				</DropdownItem>
			)}
		</Subscribe>
	);
};
